import { createContext, useContext, useState } from 'react'

import { copy } from '../Copy'

import type { CustomInsight, NewCustomInsight } from '../types/CustomInsight'

interface RulesEngineContextTypes {
  copy: typeof copy
  customInsights: CustomInsight[]
  newCustomInsight: [
    NewCustomInsight | null,
    React.Dispatch<React.SetStateAction<NewCustomInsight | null>>,
  ]
}

export const RulesEngineContext = createContext<RulesEngineContextTypes | null>(null)

type RulesEngineDataProviderProps = React.PropsWithChildren<{
  copy: typeof copy
  customInsights: CustomInsight[]
}>

export const RulesEngineDataProvider: React.FC<RulesEngineDataProviderProps> = ({
  children,
  copy,
  customInsights,
}) => {
  const [newCustomInsight, setNewCustomInsight] = useState<NewCustomInsight | null>({}) // Allows us to use the context hooks as a state store

  const store: RulesEngineContextTypes = {
    copy,
    customInsights,
    newCustomInsight: [newCustomInsight, setNewCustomInsight],
  }

  return <RulesEngineContext.Provider value={store}>{children}</RulesEngineContext.Provider>
}

export const useDataContext = () => {
  const context = useContext(RulesEngineContext)

  if (!context) {
    throw new Error('useDataContext() must be used within RulesEngineDataProvider')
  }

  return context
}

export const useCopy = () => useDataContext().copy
export const useCustomInsights = () => useDataContext().customInsights
interface UseNewCustomInsightReturn {
  0: NewCustomInsight | null
  1: React.Dispatch<React.SetStateAction<NewCustomInsight | null>>
}

export const useNewCustomInsight = (): UseNewCustomInsightReturn =>
  useDataContext().newCustomInsight
